<template>
  <div class="as-incident-list" v-if="incidents.data?.length">
    <div class="as-incident-list__head">
      <div class="as-head-current" v-if="incidents.match">
        <div class="as-match-time">
          {{ formatLocalTime(incidents.match.match_time, "DD/MM/YYYY h:mm") }}
        </div>
        <div class="as-match-status">
          {{ getLableTitle(incidents.match.status_id) }}
        </div>
      </div>
      <div class="as-head-scores-round" v-if="rounds">
        <div
          class="as-head-scores-round__items"
          v-for="(incident, key) in rounds"
          :key="key"
        >
          <div class="as-round-name">
            {{ getLableTitle(incident.set) }}
          </div>
          <div class="as-round-scores">
            <div
              class="as-round-scores__as-home"
              :class="{
                'as-win':
                  getScoresInMatch(incident.set)[0] >
                  getScoresInMatch(incident.set)[1],
              }"
            >
              {{ getScoresInMatch(incident.set)[0] }}
            </div>
            <div
              class="as-round-scores__as-away"
              :class="{
                'as-win':
                  getScoresInMatch(incident.set)[0] <
                  getScoresInMatch(incident.set)[1],
              }"
            >
              {{ getScoresInMatch(incident.set)[1] }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="as-incident-list__body" v-if="rows">
      <div
        class="as-incident__item"
        v-for="(incident, index) in rows"
        :key="index"
      >
        <h3
          @click="changeActiveCollapse(index.toString())"
          :class="{
            'as-active': activeCollapse === index.toString(),
          }"
        >
          <span>
            {{ getLableIndex(incident.set) }}: {{ getScoresInMatch(incident.set)[0] }} - {{ getScoresInMatch(incident.set)[1] }}
          </span>
          <ArrowDown fill="#4CF446" />
        </h3>
        <div
          :class="`as-incident_row ${item._class}`"
          v-if="incident.scores.length"
          v-for="(item, key) in incident.scores"
          :key="key"
          v-show="activeCollapse == index.toString()"
        >
          <div class="as__left row_score">
            <IconVolleyball />
          </div>
          <div class="as__right row_score">
            <div>
                <span :class="{
                'as-win': item.point_active === 'home',
              }">
                {{ item.home_score }}
              </span> -
              <span :class="{
                'as-win': item.point_active === 'away',
              }">
                {{ item.away_score }}
              </span>
            </div>
            <div v-if="item.tie" class="match_history_row" :class="{
              'as-green': item._class === 'as-away' && item.point_active === 'away',
            }">+{{ item.tie }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <NoData v-else />
</template>

<script setup lang="ts">
import { defineProps, ref, computed } from "vue";
import ArrowDown from "~src/components/icons/ArrowDown.vue";
import NoData from "~src/components/NoData.vue";
import IconVolleyball from "~src/components/icons/IconVolleyball.vue";
import type {
  IIncidentData,
  IIncidentRealTime,
  IRealTime,
} from "~src/interfaces/badminton";
import MatchEnum from "~src/enums/volleyball/MatchEnum";
import { formatLocalTime } from "~src/helpers/CommonHelper";

interface IProps {
  incidents: IIncidentRealTime;
}

const props = withDefaults(defineProps<IProps>(), {});
const activeCollapse = ref("0");

const changeActiveCollapse = (index: any) => {
  if (activeCollapse.value === index) {
    activeCollapse.value = "";
  } else {
    activeCollapse.value = index;
  }
};

const processIncidents = (incidents: IIncidentData[], reverse: boolean = false) => {
  const _incidents = incidents
    .sort((a: IIncidentData, b: IIncidentData) => Number(a.set) - Number(b.set))
    .map((item: any) => {
      let _scores = item.scores;
      if (_scores) {
        _scores.map((score: IRealTime, index: number) => {
          let _class = "";
          let _prev = index - 1 < 0 ? 0 : index - 1;
          let point_active = '';

          if (index) {
            if (
              score.home_score == _scores[index - 1].home_score &&
              score.away_score > _scores[index - 1].away_score
            ) {
              _class = "as-away";
            }
          } else {
            if (score.away_score > score.home_score) {
              _class = "as-away";
            }
          }

          const current_home = _scores[index].home_score;
          const current_away = _scores[index].away_score;
          const prev_home = index > 0 ? _scores[_prev].home_score : 0;
          const prev_away = index > 0 ? _scores[_prev].away_score : 0;

          // point_active;
          if (current_home > prev_home && current_away == prev_away) {
            point_active = 'home';
          }

          if (current_home == prev_home && current_away > prev_away) {
            point_active = 'away';
          }

          score._class = _class;
          score.point_active = point_active;
          score.tie = Math.abs(score.home_score - score.away_score);
        });
      }
      return {
        ...item,
        scores: _scores.slice().reverse(),
      };
    });


    return reverse ? _incidents.reverse() : _incidents;
};

const rounds = computed(() => {
  const incidents = props.incidents.data;
  return processIncidents(incidents);
});

const rows = computed(() => {
  const incidents = props.incidents.data;

  return processIncidents(incidents, true);
});

const getLableTitle = (set: number | string) => {
  const text = MatchEnum.MATCH_STATUS_TEXT as { [key: string]: any };
  return text[set] ?? "FT";
};

const getLableIndex = (set: number | string, get_text = true) => {
  const text = {
    [MatchEnum.FIRST_SET.toString()]: 1,
    [MatchEnum.SECOND_SET.toString()]: 2,
    [MatchEnum.THIRD_SET.toString()]: 3,
    [MatchEnum.FOURTH_SET.toString()]: 4,
    [MatchEnum.FIFTH_SET.toString()]: 5,
  } as { [key: string]: any };

  if (get_text) {
    return text[set] ? `SET ${text[set]}` : "";
  } else {
    return text[set] ?? 0;
  }
};

const matchScores = computed(() => {
  return props.incidents.match?.scores ?? {};
});

const getScoresInMatch = (set: number | string) => {
  const index = getLableIndex(set, false);
  const value = matchScores.value[`p${index}`] || ["", ""];

  return value;
};

</script>

<script lang="ts">
export default {};
</script>
